import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useSystemUsers } from '@Purchasing/Hooks/useSystemUsers/useSystemUsers'
import { Check, ChevronDown, Search, UserRound, UserX, Users } from 'lucide-react'
import { ReactNode, useMemo, useState } from 'react'
import { UserAvatar } from './UserAvatar'

/** Valor do filtro para processos sem responsável (o mesmo da API). */
export const UNASSIGNED = 'none'

interface AssigneeFilterProps {
    /** Ids selecionados (uid do Firebase e/ou UNASSIGNED). Vazio = todos. */
    value: string[]
    onChange: (value: string[]) => void
}

const Option = ({ selected, onClick, children, testId, label }: { selected: boolean; onClick: () => void; children: ReactNode; testId?: string; label?: string }) => (
    <button
        type="button"
        aria-label={label}
        role="menuitemcheckbox"
        aria-checked={selected}
        data-testid={testId}
        onClick={onClick}
        className={cn(
            'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent',
            selected && 'font-semibold text-brand-deep'
        )}
    >
        {children}
        <Check className={cn('ml-auto h-4 w-4 shrink-0', selected ? 'opacity-100' : 'opacity-0')} />
    </button>
)

/**
 * Filtro por responsável no estilo do comercial: "Todos", "Só os meus",
 * "Sem responsável" e a lista de usuários do sistema, com várias escolhas.
 */
export const AssigneeFilter = ({ value, onChange }: AssigneeFilterProps) => {
    const { users, currentUserId } = useSystemUsers()
    const [query, setQuery] = useState('')
    const selected = new Set(value)
    const onlyMine = value.length === 1 && value[0] === currentUserId

    const label = useMemo(() => {
        if (!value.length) return 'Todos os responsáveis'
        if (onlyMine) return 'Só os meus'
        const people = value.filter((id) => id !== UNASSIGNED)
        const withUnassigned = people.length !== value.length
        const peopleLabel =
            people.length === 0
                ? ''
                : people.length === 1
                  ? people[0] === currentUserId
                      ? 'Meus'
                      : users.find((u) => u.id === people[0])?.name ?? '1 responsável'
                  : `${people.length} responsáveis`
        if (!peopleLabel) return 'Sem responsável'
        return withUnassigned ? `${peopleLabel} + sem responsável` : peopleLabel
    }, [value, onlyMine, users, currentUserId])

    const toggle = (id: string) => {
        const next = new Set(selected)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        onChange(Array.from(next))
    }

    const visible = users.filter((user) =>
        !query.trim() || `${user.name} ${user.mail ?? ''}`.toLowerCase().includes(query.trim().toLowerCase())
    )

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    aria-label="Filtrar responsável"
                    data-testid="assignee-filter"
                    className={cn(
                        'inline-flex h-9 min-w-[160px] items-center gap-2 rounded-full border border-border bg-secondary px-3 text-sm',
                        value.length > 0 && 'border-brand bg-accent text-accent-foreground'
                    )}
                >
                    <Users className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                    <span className="truncate">{label}</span>
                    <ChevronDown className="ml-auto h-4 w-4 shrink-0 opacity-60" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-2">
                <div className="flex flex-col gap-0.5" role="menu" aria-label="Responsáveis">
                    <Option selected={!value.length} onClick={() => onChange([])} testId="assignee-filter-all">
                        <Users className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />Todos
                    </Option>
                    {currentUserId && (
                        <Option selected={onlyMine} onClick={() => onChange(onlyMine ? [] : [currentUserId])} testId="assignee-filter-mine">
                            <UserRound className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />Só os meus
                        </Option>
                    )}
                    <Option selected={selected.has(UNASSIGNED)} onClick={() => toggle(UNASSIGNED)} testId="assignee-filter-none">
                        <UserX className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />Sem responsável
                    </Option>
                </div>
                <div className="my-2 border-t border-border" />
                <div className="relative mb-1">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
                    <Input aria-label="Buscar usuário" placeholder="Buscar usuário" className="h-8 pl-8 text-sm" value={query} onChange={(e) => setQuery(e.target.value)} />
                </div>
                <div className="max-h-64 overflow-y-auto" role="menu" aria-label="Usuários">
                    {visible.map((user) => (
                        <Option key={user.id} label={user.name} selected={selected.has(user.id)} onClick={() => toggle(user.id)}>
                            <UserAvatar name={user.name} className="h-6 w-6 text-[10px]" />
                            <span className="truncate">{user.name}{user.id === currentUserId && <span className="font-normal text-muted-foreground"> (você)</span>}</span>
                        </Option>
                    ))}
                    {!visible.length && <p className="px-2 py-1.5 text-sm text-muted-foreground">Nenhum usuário encontrado.</p>}
                </div>
                {value.length > 0 && (
                    <Button type="button" variant="ghost" size="sm" className="mt-1 w-full" onClick={() => onChange([])}>Limpar filtro</Button>
                )}
            </PopoverContent>
        </Popover>
    )
}
